import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Label } from '../../components/ui/label';
import {
  Clock,
  Calendar,
  Buildings,
  Play,
  Pause,
  Trash,
  CheckCircle,
  XCircle,
  CircleNotch,
  Plus,
  PencilSimple,
  Lightning,
  Bell,
  EnvelopeSimple,
  ArrowClockwise,
  CaretRight
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' }
];

export default function BackupScheduleManager({ clientId, clientName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  
  const [formData, setFormData] = useState({
    frequency: 'weekly',
    time_utc: '02:00',
    day_of_week: 'sunday',
    day_of_month: 1,
    enabled: true,
    notify_email: '',
    notify_on_success: true,
    notify_on_failure: true,
    retention_count: 5
  });

  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/scheduled-backup/client/${clientId}/schedule`);
      if (response.data.schedule) {
        setSchedule(response.data.schedule);
        setFormData({
          frequency: response.data.schedule.frequency,
          time_utc: response.data.schedule.time_utc,
          day_of_week: response.data.schedule.day_of_week || 'sunday',
          day_of_month: response.data.schedule.day_of_month || 1,
          enabled: response.data.schedule.enabled,
          notify_email: response.data.schedule.notify_email || '',
          notify_on_success: response.data.schedule.notify_on_success,
          notify_on_failure: response.data.schedule.notify_on_failure,
          retention_count: response.data.schedule.retention_count
        });
      }
    } catch (error) {
      console.error('Failed to fetch schedule:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (schedule) {
        // Update existing
        await axios.patch(`${API_URL}/scheduled-backup/schedules/${schedule.id}`, formData);
        toast.success('Schedule updated');
      } else {
        // Create new
        await axios.post(`${API_URL}/scheduled-backup/schedules`, {
          client_id: clientId,
          ...formData
        });
        toast.success('Schedule created');
      }
      setShowForm(false);
      fetchSchedule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!schedule) return;
    if (!confirm('Are you sure you want to delete this backup schedule?')) return;
    
    try {
      await axios.delete(`${API_URL}/scheduled-backup/schedules/${schedule.id}`);
      toast.success('Schedule deleted');
      setSchedule(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete schedule');
    }
  };

  const handleTriggerNow = async () => {
    if (!schedule) return;
    setTriggering(true);
    try {
      await axios.post(`${API_URL}/scheduled-backup/schedules/${schedule.id}/run`);
      toast.success('Backup triggered! Check back shortly for results.');
      fetchSchedule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger backup');
    } finally {
      setTriggering(false);
    }
  };

  const handleToggleEnabled = async () => {
    if (!schedule) return;
    try {
      await axios.patch(`${API_URL}/scheduled-backup/schedules/${schedule.id}`, {
        enabled: !schedule.enabled
      });
      toast.success(schedule.enabled ? 'Schedule paused' : 'Schedule resumed');
      fetchSchedule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update schedule');
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('en-AE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFrequencyLabel = (freq, dayOfWeek, dayOfMonth, timeUtc) => {
    const time = timeUtc || '02:00';
    switch (freq) {
      case 'daily':
        return `Every day at ${time} UTC`;
      case 'weekly':
        return `Every ${dayOfWeek || 'Sunday'} at ${time} UTC`;
      case 'monthly':
        return `Monthly on day ${dayOfMonth || 1} at ${time} UTC`;
      default:
        return freq;
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="backup-schedule-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#0f172a] flex items-center justify-center">
            <Clock size={20} className="text-[#d4a84b]" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Backup Schedule</h3>
            <p className="text-sm text-muted-foreground">{clientName}</p>
          </div>
        </div>
        {schedule && (
          <Badge className={schedule.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
            {schedule.enabled ? 'Active' : 'Paused'}
          </Badge>
        )}
      </div>

      {schedule ? (
        // Show existing schedule
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Schedule Info Card */}
          <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Frequency</span>
              <span className="font-medium">
                {getFrequencyLabel(
                  schedule.frequency,
                  schedule.day_of_week,
                  schedule.day_of_month,
                  schedule.time_utc
                )}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Next Run</span>
              <span className="font-medium">
                {schedule.enabled ? formatDateTime(schedule.next_run_at) : 'Paused'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Last Run</span>
              <span className="font-medium flex items-center gap-2">
                {schedule.last_run_at ? (
                  <>
                    {formatDateTime(schedule.last_run_at)}
                    {schedule.last_run_status === 'completed' ? (
                      <CheckCircle size={16} className="text-emerald-500" weight="fill" />
                    ) : schedule.last_run_status === 'failed' ? (
                      <XCircle size={16} className="text-red-500" weight="fill" />
                    ) : null}
                  </>
                ) : (
                  'Never'
                )}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Retention</span>
              <span className="font-medium">Keep last {schedule.retention_count} backups</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Notifications</span>
              <span className="font-medium flex items-center gap-1">
                <EnvelopeSimple size={14} />
                {schedule.notify_email || 'Not set'}
              </span>
            </div>
          </div>

          {/* Recent Runs */}
          {schedule.recent_runs && schedule.recent_runs.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <ArrowClockwise size={14} />
                Recent Runs
              </h4>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Size</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schedule.recent_runs.map((run) => (
                      <TableRow key={run.id}>
                        <TableCell className="text-sm">
                          {formatDateTime(run.started_at)}
                        </TableCell>
                        <TableCell>
                          {run.status === 'completed' ? (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle size={12} className="mr-1" /> Completed
                            </Badge>
                          ) : run.status === 'failed' ? (
                            <Badge variant="destructive">
                              <XCircle size={12} className="mr-1" /> Failed
                            </Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-700">
                              <CircleNotch size={12} className="mr-1 animate-spin" /> Running
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {run.size_bytes ? `${(run.size_bytes / 1024).toFixed(1)} KB` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleEnabled}
            >
              {schedule.enabled ? (
                <><Pause size={14} className="mr-1" /> Pause</>
              ) : (
                <><Play size={14} className="mr-1" /> Resume</>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowForm(true)}
            >
              <PencilSimple size={14} className="mr-1" /> Edit
            </Button>
            <Button
              size="sm"
              onClick={handleTriggerNow}
              disabled={triggering}
              className="bg-[#0f172a] hover:bg-[#1e293b]"
            >
              {triggering ? (
                <CircleNotch size={14} className="mr-1 animate-spin" />
              ) : (
                <Lightning size={14} className="mr-1" />
              )}
              Run Now
            </Button>
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <Trash size={14} className="mr-1" /> Delete
            </Button>
          </div>
        </motion.div>
      ) : (
        // No schedule - show create button
        <div className="text-center py-8">
          <Clock size={48} className="mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground mb-4">No backup schedule configured</p>
          <Button onClick={() => setShowForm(true)}>
            <Plus size={16} className="mr-2" />
            Create Schedule
          </Button>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock size={20} />
              {schedule ? 'Edit Backup Schedule' : 'Create Backup Schedule'}
            </DialogTitle>
            <DialogDescription>
              Configure automatic backups for {clientName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Frequency */}
            <div className="space-y-2">
              <Label>Backup Frequency</Label>
              <Select
                value={formData.frequency}
                onValueChange={(v) => setFormData({ ...formData, frequency: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map((freq) => (
                    <SelectItem key={freq.value} value={freq.value}>
                      {freq.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Day of Week (for weekly) */}
            {formData.frequency === 'weekly' && (
              <div className="space-y-2">
                <Label>Day of Week</Label>
                <Select
                  value={formData.day_of_week}
                  onValueChange={(v) => setFormData({ ...formData, day_of_week: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((day) => (
                      <SelectItem key={day.value} value={day.value}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Day of Month (for monthly) */}
            {formData.frequency === 'monthly' && (
              <div className="space-y-2">
                <Label>Day of Month</Label>
                <Select
                  value={String(formData.day_of_month)}
                  onValueChange={(v) => setFormData({ ...formData, day_of_month: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                      <SelectItem key={day} value={String(day)}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Time */}
            <div className="space-y-2">
              <Label>Time (UTC)</Label>
              <Select
                value={formData.time_utc}
                onValueChange={(v) => setFormData({ ...formData, time_utc: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => {
                    const hour = i.toString().padStart(2, '0');
                    return (
                      <SelectItem key={hour} value={`${hour}:00`}>
                        {hour}:00 UTC
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Retention */}
            <div className="space-y-2">
              <Label>Keep Last N Backups</Label>
              <Select
                value={String(formData.retention_count)}
                onValueChange={(v) => setFormData({ ...formData, retention_count: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[3, 5, 7, 10, 15, 30].map((count) => (
                    <SelectItem key={count} value={String(count)}>
                      {count} backups
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notifications */}
            <div className="space-y-4 pt-2 border-t">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Bell size={14} />
                Email Notifications
              </h4>
              
              <div className="space-y-2">
                <Label>Notification Email</Label>
                <Input
                  type="email"
                  value={formData.notify_email}
                  onChange={(e) => setFormData({ ...formData, notify_email: e.target.value })}
                  placeholder="your@email.com"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="notify-success" className="cursor-pointer">
                  Notify on success
                </Label>
                <Switch
                  id="notify-success"
                  checked={formData.notify_on_success}
                  onCheckedChange={(v) => setFormData({ ...formData, notify_on_success: v })}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="notify-failure" className="cursor-pointer">
                  Notify on failure
                </Label>
                <Switch
                  id="notify-failure"
                  checked={formData.notify_on_failure}
                  onCheckedChange={(v) => setFormData({ ...formData, notify_on_failure: v })}
                />
              </div>
            </div>

            {/* Enabled */}
            <div className="flex items-center justify-between pt-2">
              <Label htmlFor="schedule-enabled" className="cursor-pointer">
                Enable schedule
              </Label>
              <Switch
                id="schedule-enabled"
                checked={formData.enabled}
                onCheckedChange={(v) => setFormData({ ...formData, enabled: v })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <CircleNotch size={16} className="animate-spin mr-2" />
              ) : null}
              {schedule ? 'Update Schedule' : 'Create Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
